import { useEffect, useMemo, useState } from 'react'
import { X, BookOpen, Target, Search as SearchIcon } from 'lucide-react'
import { buildSearchIndex, searchIndex } from '../lib/searchIndex'
import { Button } from './ui/button'

export function SearchModal({ isOpen, onClose, onNavigate }) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  
  // Build search index once on mount
  const index = useMemo(() => buildSearchIndex(), [])
  
  // Search when query changes
  const results = useMemo(() => {
    if (!query.trim()) return []
    return searchIndex(index, query)
  }, [index, query])

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [results])

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault()
        const result = results[selectedIndex]
        if (result.type === 'standard') {
          onNavigate(`/standards?code=${encodeURIComponent(result.code)}`)
        } else if (result.type === 'lesson') {
          // Navigate to curriculum with appropriate filters
          const params = new URLSearchParams()
          if (result.subject === 'ELA' || result.subject?.includes('ELA')) {
            params.set('subject', 'ela')
          } else if (result.subject === 'SS' || result.subject?.includes('Social')) {
            params.set('subject', 'ss')
          }
          if (result.gradeLabel) {
            params.set('grade', result.gradeLabel.replace(/\D/g, '') || 'k')
          }
          if (result.unit) {
            params.set('unit', result.unit)
          }
          onNavigate(`/curriculum?${params.toString()}`)
        }
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, results, selectedIndex, onClose, onNavigate])

  // Clear query when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      setSelectedIndex(0)
    }
  }, [isOpen])

  const handleSelectResult = (result) => {
    if (result.type === 'standard') {
      onNavigate(`/standards?code=${encodeURIComponent(result.code)}`)
    } else if (result.type === 'lesson') {
      // Navigate to curriculum with appropriate filters
      const params = new URLSearchParams()
      if (result.subject === 'ELA' || result.subject?.includes('ELA')) {
        params.set('subject', 'ela')
      } else if (result.subject === 'SS' || result.subject?.includes('Social')) {
        params.set('subject', 'ss')
      }
      if (result.gradeLabel) {
        params.set('grade', result.gradeLabel.replace(/\D/g, '') || 'k')
      }
      if (result.unit) {
        params.set('unit', result.unit)
      }
      onNavigate(`/curriculum?${params.toString()}`)
    }
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-x-0 top-20 mx-auto max-w-3xl z-50 px-4">
        <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b">
            <SearchIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search lessons, standards, units..."
              className="flex-1 outline-none text-lg"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {query.trim() === '' && (
              <div className="px-4 py-8 text-center text-gray-500">
                <SearchIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Start typing to search across all lessons and standards</p>
              </div>
            )}

            {query.trim() !== '' && results.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500">
                <p className="text-sm">No results found for "{query}"</p>
              </div>
            )}

            {results.length > 0 && (
              <div className="divide-y">
                {results.map((result, idx) => (
                  <button
                    key={`${result.type}-${result.code || result.key}-${idx}`}
                    onClick={() => handleSelectResult(result)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                      idx === selectedIndex ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {result.type === 'standard' ? (
                        <Target className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <BookOpen className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        {result.type === 'standard' && (
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {result.code}
                              </span>
                              <span className="text-xs text-gray-500">
                                {result.subject} • {result.gradeLabel}
                              </span>
                            </div>
                            <p className="text-sm text-gray-800 line-clamp-2">
                              {result.statement}
                            </p>
                          </>
                        )}
                        
                        {result.type === 'lesson' && (
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                result.subject?.includes('ELA') ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                              }`}>
                                {result.subject} {result.gradeLabel} • Unit {result.unit} • Lesson {result.lesson}
                              </span>
                            </div>
                            <p className="text-sm font-medium text-gray-900 mb-1 line-clamp-1">
                              {result.title}
                            </p>
                            {result.essentialQuestion && typeof result.essentialQuestion === 'string' && (
                              <p className="text-xs text-gray-600 line-clamp-2">
                                {result.essentialQuestion}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer hint */}
          {results.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500 flex items-center gap-4">
              <span>↑↓ Navigate</span>
              <span>↵ Select</span>
              <span>Esc Close</span>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
